export const MATH_SKILLS = [
  "counting",
  "sequence",
  "addition",
  "subtraction",
  "missing-number",
  "compare",
  "tens",
  "word-problems",
] as const;

export type MathSkill = (typeof MATH_SKILLS)[number];

export const MATH_TIERS = [1, 2, 3, 4, 5, 6] as const;

export type MathTier = (typeof MATH_TIERS)[number];

export type ComparisonSymbol = "<" | "=" | ">";

export type MathAnswer = number | ComparisonSymbol;

export type ChoiceId = "choice-1" | "choice-2" | "choice-3";

export interface AnswerChoice {
  readonly id: ChoiceId;
  readonly value: MathAnswer;
  readonly label: string;
}

export type VisualAid =
  | {
      readonly kind: "objects";
      readonly count: number;
      readonly icon: "gem" | "star";
      readonly arrangement: "line" | "rows" | "scattered";
    }
  | {
      readonly kind: "object-groups";
      readonly groups: readonly [number, number];
      readonly icon: "gem" | "star";
    }
  | {
      readonly kind: "object-removal";
      readonly start: number;
      readonly removed: number;
      readonly icon: "gem" | "star";
    }
  | {
      readonly kind: "number-line";
      readonly min: number;
      readonly max: number;
      readonly highlighted: readonly number[];
    }
  | {
      readonly kind: "base-ten";
      readonly tens: number;
      readonly ones: number;
    };

export type ChallengeBand = "review" | "current" | "stretch";

/**
 * A presentation-ready Grade-1 question. `expressionLTR` must always be rendered
 * with `dir="ltr"`, even inside the Hebrew interface.
 */
export interface MathQuestion {
  readonly id: string;
  readonly signature: string;
  readonly skill: MathSkill;
  readonly tier: MathTier;
  readonly challengeBand: ChallengeBand;
  readonly supportMode: boolean;
  readonly promptHebrew: string;
  readonly spokenHebrew: string;
  readonly expressionLTR: string;
  readonly visualAid?: VisualAid;
  readonly choices: readonly [AnswerChoice, AnswerChoice, AnswerChoice];
  readonly correctAnswer: MathAnswer;
  readonly correctChoiceId: ChoiceId;
}

export interface QuestionRequest {
  readonly skill: MathSkill;
  readonly tier: MathTier;
  readonly challengeBand?: ChallengeBand;
  readonly supportMode?: boolean;
}

export interface SkillMasteryState {
  readonly currentTier: MathTier;
  readonly recentResults: readonly boolean[];
  readonly consecutiveMisses: number;
  readonly supportActive: boolean;
  readonly totalAttempts: number;
  readonly totalCorrect: number;
}

export type MasteryBySkill = Readonly<Record<MathSkill, SkillMasteryState>>;

export interface AnswerFeedback {
  readonly correct: boolean;
  readonly correctAnswer: MathAnswer;
  readonly promoted: boolean;
  readonly supportActivated: boolean;
  readonly supportActive: boolean;
  readonly previousTier: MathTier;
  readonly currentTier: MathTier;
}

export function clampMathTier(value: number): MathTier {
  if (value <= 1) return 1;
  if (value >= 6) return 6;
  return Math.round(value) as MathTier;
}
